<template>
    <div class="w-full">
        <VcSelect
            v-model="selectValue"
            :options="options"
            :config="{
                labelKey: 'label',
                valueKey: 'value'
            }"
            filterable
            class="w-full"
        />
        <VcFormItem
            v-if="isCustom"
            :label="String(customInputLabel || '')"
            class="mt-3"
        >
            <template
                #info-text
            >
                <a
                    :href="props.customInputTooltipLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 hover:text-blue-800 underline text-xs p-4"
                >
                    {{ tooltipInputText }}
                </a>
            </template>
            <VcInput
                v-model="customValue"
                :placeholder="String(customInputPlaceholder || '')"
            />
        </VcFormItem>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { VcSelect, VcInput, VcFormItem } from '@voicenter-team/voicenter-ui-plus'
import { useI18n } from 'vue-i18n'
import { CUSTOM_FORMAT_VALUE } from '@/enum/dateTimeFormats'
interface Option {
    value: string
    label: string
}

interface Props {
    value?: string
    options: Option[]
    defaultValue?: string
    customInputLabel?: string
    customInputPlaceholder?: string
    customInputTooltipLink?: string
    customInputTooltipText?: string
}

const props = withDefaults(defineProps<Props>(), {
    value: undefined,
    defaultValue: '',
    customInputLabel: () => '',
    customInputPlaceholder: () => '',
    customInputTooltipLink: 'https://day.js.org/docs/en/display/format',
    customInputTooltipText: undefined
})

const { t } = useI18n()

const tooltipInputText = computed(() =>
    String(props.customInputTooltipText ?? t('widget.clock.documentation'))
)

const emit = defineEmits<{
    (e: 'update:value', value: string | undefined): void
}>()

const isCustom = ref(false)

const selectValue = computed<string>({
    get () {
        const currentValue = props.value
        if (isCustom.value) return CUSTOM_FORMAT_VALUE
        if (!currentValue) return props.defaultValue || ''
        const isInOptions = props.options.some(opt => opt.value === currentValue)
        if (!isInOptions) {
            isCustom.value = true
            return CUSTOM_FORMAT_VALUE
        }
        return currentValue
    },
    set (value: string) {
        if (value === CUSTOM_FORMAT_VALUE) {
            isCustom.value = true
            const currentValue = props.value
            if (currentValue && props.options.some(opt => opt.value === currentValue)) {
                emit('update:value', '')
            }
        } else {
            isCustom.value = false
            emit('update:value', value)
        }
    }
})

const customValue = computed({
    get () {
        const currentValue = props.value
        if (!currentValue) return ''
        const isInOptions = props.options.some(opt => opt.value === currentValue)
        if (!isInOptions) {
            return currentValue
        }
        return ''
    },
    set (value: string) {
        emit('update:value', value || undefined)
    }
})
</script>
